import React, { memo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Timer, Bell, Music, Volume2, VolumeX,
  Play, Pause, Trash2, Clock, Plus, Minus,
  Coffee, Dumbbell, Brain, Headphones, CloudRain, Radio
} from 'lucide-react';
import { Z_INDEX } from '../../constants/zIndex';

/**
 * DeviceActionsSheet - Contrôle des actions sur l'appareil
 * Minuteurs, alarmes, musique d'ambiance, etc.
 */
export const DeviceActionsSheet = memo(function DeviceActionsSheet({
  isOpen,
  onClose,
  deviceActions
}) {
  const [activeTab, setActiveTab] = useState('timer'); // 'timer', 'alarm', 'music'
  const [timerMinutes, setTimerMinutes] = useState(25);
  const [timerLabel, setTimerLabel] = useState('');
  const [alarmTime, setAlarmTime] = useState('07:00');
  const [alarmLabel, setAlarmLabel] = useState('');

  const {
    timers = [],
    alarms = [],
    isPlaying,
    currentMusic,
    notificationPermission,
    requestNotificationPermission,
    setTimer,
    pauseTimer,
    cancelTimer,
    setAlarm,
    cancelAlarm,
    playAmbientMusic,
    stopMusic,
    speak,
    formatTime
  } = deviceActions || {};

  // Presets de minuteur
  const timerPresets = [
    { minutes: 5, label: 'Pause rapide', icon: Coffee, color: 'amber' },
    { minutes: 15, label: 'Petite tâche', icon: Clock, color: 'blue' },
    { minutes: 25, label: 'Pomodoro', icon: Brain, color: 'red' },
    { minutes: 45, label: 'Focus profond', icon: Dumbbell, color: 'purple' },
    { minutes: 60, label: '1 heure', icon: Timer, color: 'emerald' },
  ];

  // Types de musique
  const musicTypes = [
    { id: 'lofi', label: 'Lo-Fi', icon: Headphones, color: 'pink' },
    { id: 'nature', label: 'Nature', icon: CloudRain, color: 'emerald' },
    { id: 'focus', label: 'Focus', icon: Brain, color: 'blue' },
    { id: 'white_noise', label: 'Bruit blanc', icon: Radio, color: 'zinc' },
  ];

  // Créer un minuteur
  const handleCreateTimer = () => {
    if (timerMinutes > 0) {
      setTimer(timerMinutes, timerLabel || `Minuteur ${timerMinutes}min`);
      setTimerLabel('');
    }
  };

  // Créer une alarme
  const handleCreateAlarm = () => {
    if (alarmTime) {
      setAlarm(alarmTime, alarmLabel || `Alarme ${alarmTime}`);
      setAlarmLabel('');
    }
  };

  // Demander permission si nécessaire
  const handleRequestPermission = async () => {
    const granted = await requestNotificationPermission();
    if (granted) {
      speak('Notifications activées !');
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm"
            style={{ zIndex: Z_INDEX.MODAL_BACKDROP }}
          />

          {/* Sheet */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed bottom-0 left-0 right-0 max-h-[95svh] overflow-y-auto"
            style={{ zIndex: Z_INDEX.MOOD_CHECK_IN }}
          >
            <div className="bg-zinc-900 rounded-t-3xl border-t border-zinc-800">
              {/* Handle */}
              <div className="flex justify-center pt-3 pb-2">
                <div className="w-10 h-1 bg-zinc-700 rounded-full" />
              </div>

              {/* Header */}
              <div className="px-5 pb-4 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-white">Actions appareil</h2>
                  <p className="text-xs text-zinc-500">Minuteurs, alarmes et musique</p>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 rounded-full bg-zinc-800 text-zinc-400 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Permission warning */}
              {notificationPermission !== 'granted' && (
                <div className="mx-5 mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-xl">
                  <div className="flex items-center gap-3">
                    <Bell className="w-5 h-5 text-amber-400" />
                    <div className="flex-1">
                      <p className="text-sm text-amber-300 font-medium">Notifications désactivées</p>
                      <p className="text-xs text-amber-400/70">Active-les pour les rappels</p>
                    </div>
                    <button
                      onClick={handleRequestPermission}
                      className="px-3 py-1.5 bg-amber-500 text-black text-xs font-bold rounded-lg"
                    >
                      Activer
                    </button>
                  </div>
                </div>
              )}

              {/* Tabs */}
              <div className="px-5 mb-4">
                <div className="flex bg-zinc-800/50 rounded-xl p-1">
                  {[
                    { id: 'timer', label: 'Minuteur', icon: Timer },
                    { id: 'alarm', label: 'Alarme', icon: Bell },
                    { id: 'music', label: 'Musique', icon: Music },
                  ].map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-all ${
                        activeTab === tab.id
                          ? 'bg-zinc-700 text-white'
                          : 'text-zinc-400 hover:text-zinc-300'
                      }`}
                    >
                      <tab.icon className="w-4 h-4" />
                      <span>{tab.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Content */}
              <div className="px-5 pb-6 overflow-y-auto max-h-[60vh]">
                {/* === MINUTEUR === */}
                {activeTab === 'timer' && (
                  <div className="space-y-4">
                    {/* Presets */}
                    <div>
                      <p className="text-xs text-zinc-500 mb-2 uppercase tracking-wide">Presets</p>
                      <div className="grid grid-cols-3 gap-2">
                        {timerPresets.map(preset => (
                          <button
                            key={preset.minutes}
                            onClick={() => {
                              setTimer(preset.minutes, preset.label);
                            }}
                            className={`p-3 rounded-xl bg-${preset.color}-500/10 border border-${preset.color}-500/30 hover:bg-${preset.color}-500/20 transition-all`}
                          >
                            <preset.icon className={`w-5 h-5 text-${preset.color}-400 mx-auto mb-1`} />
                            <p className="text-xs font-medium text-zinc-300">{preset.minutes}min</p>
                            <p className="text-[10px] text-zinc-500 truncate">{preset.label}</p>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Custom timer */}
                    <div className="p-4 bg-zinc-800/50 rounded-xl space-y-3">
                      <p className="text-xs text-zinc-500 uppercase tracking-wide">Personnalisé</p>

                      {/* Minutes selector */}
                      <div className="flex items-center justify-center gap-4">
                        <button
                          onClick={() => setTimerMinutes(m => Math.max(1, m - 5))}
                          className="p-3 rounded-full bg-zinc-700 text-zinc-300 hover:bg-zinc-600"
                        >
                          <Minus className="w-5 h-5" />
                        </button>
                        <div className="text-center">
                          <span className="text-4xl font-bold text-white tabular-nums">
                            {timerMinutes}
                          </span>
                          <span className="text-lg text-zinc-500 ml-1">min</span>
                        </div>
                        <button
                          onClick={() => setTimerMinutes(m => Math.min(180, m + 5))}
                          className="p-3 rounded-full bg-zinc-700 text-zinc-300 hover:bg-zinc-600"
                        >
                          <Plus className="w-5 h-5" />
                        </button>
                      </div>

                      {/* Label input */}
                      <input
                        type="text"
                        value={timerLabel}
                        onChange={(e) => setTimerLabel(e.target.value)}
                        placeholder="Nom du minuteur (optionnel)"
                        className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-sm text-zinc-300 placeholder-zinc-600"
                      />

                      {/* Start button */}
                      <button
                        onClick={handleCreateTimer}
                        className="w-full py-3 bg-emerald-500 text-white font-bold rounded-xl hover:bg-emerald-400 transition-all flex items-center justify-center gap-2"
                      >
                        <Play className="w-5 h-5" fill="currentColor" />
                        Démarrer
                      </button>
                    </div>

                    {/* Active timers */}
                    {timers.length > 0 && (
                      <div>
                        <p className="text-xs text-zinc-500 mb-2 uppercase tracking-wide">En cours</p>
                        <div className="space-y-2">
                          {timers.filter(t => !t.completed).map(timer => (
                            <div
                              key={timer.id}
                              className="p-3 bg-zinc-800 rounded-xl flex items-center gap-3"
                            >
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                timer.isPaused ? 'bg-amber-500/20' : 'bg-emerald-500/20'
                              }`}>
                                <Timer className={`w-5 h-5 ${timer.isPaused ? 'text-amber-400' : 'text-emerald-400'}`} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-zinc-200 truncate">{timer.label}</p>
                                <p className={`text-2xl font-bold tabular-nums ${
                                  timer.remaining < 60 ? 'text-red-400' : 'text-zinc-100'
                                }`}>
                                  {formatTime?.(timer.remaining) || '00:00'}
                                </p>
                              </div>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => pauseTimer(timer.id)}
                                  className="p-2 rounded-lg bg-zinc-700 text-zinc-300 hover:bg-zinc-600"
                                >
                                  {timer.isPaused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                                </button>
                                <button
                                  onClick={() => cancelTimer(timer.id)}
                                  className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* === ALARME === */}
                {activeTab === 'alarm' && (
                  <div className="space-y-4">
                    {/* Create alarm */}
                    <div className="p-4 bg-zinc-800/50 rounded-xl space-y-3">
                      <p className="text-xs text-zinc-500 uppercase tracking-wide">Nouvelle alarme</p>

                      {/* Time picker */}
                      <input
                        type="time"
                        value={alarmTime}
                        onChange={(e) => setAlarmTime(e.target.value)}
                        className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-xl text-2xl font-bold text-white text-center"
                      />

                      {/* Label */}
                      <input
                        type="text"
                        value={alarmLabel}
                        onChange={(e) => setAlarmLabel(e.target.value)}
                        placeholder="Nom de l'alarme (optionnel)"
                        className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-sm text-zinc-300 placeholder-zinc-600"
                      />

                      {/* Quick presets */}
                      <div className="flex gap-2">
                        {['06:00', '07:00', '08:00', '12:00', '18:00'].map(time => (
                          <button
                            key={time}
                            onClick={() => setAlarmTime(time)}
                            className={`flex-1 py-2 rounded-lg text-xs font-medium transition-all ${
                              alarmTime === time
                                ? 'bg-blue-500 text-white'
                                : 'bg-zinc-700 text-zinc-400 hover:bg-zinc-600'
                            }`}
                          >
                            {time}
                          </button>
                        ))}
                      </div>

                      {/* Create button */}
                      <button
                        onClick={handleCreateAlarm}
                        className="w-full py-3 bg-blue-500 text-white font-bold rounded-xl hover:bg-blue-400 transition-all flex items-center justify-center gap-2"
                      >
                        <Bell className="w-5 h-5" />
                        Programmer
                      </button>
                    </div>

                    {/* Active alarms */}
                    {alarms.length > 0 && (
                      <div>
                        <p className="text-xs text-zinc-500 mb-2 uppercase tracking-wide">Alarmes actives</p>
                        <div className="space-y-2">
                          {alarms.filter(a => a.isActive).map(alarm => (
                            <div
                              key={alarm.id}
                              className="p-3 bg-zinc-800 rounded-xl flex items-center gap-3"
                            >
                              <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                                <Bell className="w-5 h-5 text-blue-400" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-zinc-200 truncate">{alarm.label}</p>
                                <p className="text-2xl font-bold text-zinc-100 tabular-nums">{alarm.time}</p>
                              </div>
                              <button
                                onClick={() => cancelAlarm(alarm.id)}
                                className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* === MUSIQUE === */}
                {activeTab === 'music' && (
                  <div className="space-y-4">
                    <p className="text-xs text-zinc-500 uppercase tracking-wide">Musique d'ambiance</p>

                    {/* Music types */}
                    <div className="grid grid-cols-2 gap-3">
                      {musicTypes.map(music => (
                        <button
                          key={music.id}
                          onClick={() => {
                            if (isPlaying && currentMusic === music.id) {
                              stopMusic();
                            } else {
                              playAmbientMusic(music.id);
                            }
                          }}
                          className={`p-4 rounded-xl border transition-all ${
                            isPlaying && currentMusic === music.id
                              ? `bg-${music.color}-500/20 border-${music.color}-500/50`
                              : 'bg-zinc-800/50 border-zinc-700 hover:border-zinc-600'
                          }`}
                        >
                          <music.icon className={`w-8 h-8 mx-auto mb-2 ${
                            isPlaying && currentMusic === music.id
                              ? `text-${music.color}-400`
                              : 'text-zinc-400'
                          }`} />
                          <p className="text-sm font-medium text-zinc-200">{music.label}</p>
                          {isPlaying && currentMusic === music.id && (
                            <p className="text-xs text-emerald-400 mt-1">En lecture</p>
                          )}
                        </button>
                      ))}
                    </div>

                    {/* Now playing */}
                    {isPlaying && (
                      <div className="p-4 bg-zinc-800 rounded-xl">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                            <Volume2 className="w-6 h-6 text-emerald-400" />
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-zinc-200">En lecture</p>
                            <p className="text-lg font-bold text-white capitalize">{currentMusic}</p>
                          </div>
                          <button
                            onClick={stopMusic}
                            className="p-3 rounded-xl bg-red-500/20 text-red-400 hover:bg-red-500/30"
                          >
                            <VolumeX className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Info */}
                    <div className="p-3 bg-zinc-800/30 rounded-xl">
                      <p className="text-xs text-zinc-500 text-center">
                        La musique s'ouvre dans YouTube pour une lecture continue.
                        <br />
                        Reviens ici pour contrôler les minuteurs.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
});

export default DeviceActionsSheet;
